import React, { useEffect, useMemo, useState } from 'react';
import {
  Form,
  Switch,
  Card,
  Row,
  Col,
  Space,
  Tooltip,
  Button,
  List,
  Typography,
  Empty,
  Modal,
  Input,
  DatePicker,
  Divider,
  Tag,
  Select,
  Badge,
  message
} from 'antd';
import {
  BellOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useParams } from 'react-router-dom';
import { getProjectById } from '../../../api/project';
import SimpleEditor from '../../ESR/editor/texteditor/texteditor';

dayjs.extend(utc);

const monthFormat = 'MMM YYYY';

// Backend enum values
const TYPE_VALUE_ESR = 'ESR_RISK';
const TYPE_VALUE_STATUS = 'STATUS_UPDATE';

// Friendly labels
const TYPE_DISPLAY: Record<string, string> = {
  [TYPE_VALUE_ESR]: 'Potential ES Risk',
  [TYPE_VALUE_STATUS]: 'Status Update'
};

const TYPE_OPTIONS = [
  { value: TYPE_VALUE_STATUS, label: TYPE_DISPLAY[TYPE_VALUE_STATUS] },
  { value: TYPE_VALUE_ESR, label: TYPE_DISPLAY[TYPE_VALUE_ESR] }
];

const FILTER_OUT_ESR = (opts: typeof TYPE_OPTIONS) =>
  opts.filter((o) => o.value !== TYPE_VALUE_ESR);

type RatingKey = 'ghg' | 'sdg' | 'esr';
const reviewNoun: Record<RatingKey, string> = {
  ghg: 'ghg rating',
  sdg: 'sdg rating',
  esr: 'esr screening'
};

const Tile: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ border: '1px solid #f0f0f0', borderRadius: 10, padding: 12 }}>
    <div style={{ fontWeight: 600, marginBottom: 8 }}>{title}</div>
    {children}
  </div>
);

const StatusAlerts: React.FC<{ form: any; refreshKey: number }> = ({ form, refreshKey }) => {
  const { id, creditingPeriodId } = useParams<{ id; creditingPeriodId: string }>();
  const [loading, setLoading] = useState(false);

  // Project Updates add/edit modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [formUpdate] = Form.useForm();
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  // Rating review confirm modal state (custom controlled modal)
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmMode, setConfirmMode] = useState<'on' | 'off' | null>(null);
  const [confirmCode, setConfirmCode] = useState<RatingKey | null>(null);

  // Project Update save confirmation state
  const [updateConfirmOpen, setUpdateConfirmOpen] = useState(false);
  const [pendingEntry, setPendingEntry] = useState<any | null>(null);
  const [pendingIndex, setPendingIndex] = useState<number | null>(null);

  // Delete confirmation state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [esrExists, setEsrExists] = useState(false);

  const effectiveTypeOptions = useMemo(
    () => (esrExists ? TYPE_OPTIONS.filter((o) => o.value !== TYPE_VALUE_ESR) : TYPE_OPTIONS),
    [esrExists]
  );

  // Ensure base shape exists
  useEffect(() => {
    const ratings = form.getFieldValue(['ratings']) || {};
    form.setFieldsValue({
      ratings: {
        settings: { ...(ratings.settings || {}) },
        ratingUpdates: ratings.ratingUpdates || []
      }
    });
  }, [form]);

  // Fetch initial values
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const projectData = await getProjectById(id, Number(creditingPeriodId));
        const cp = projectData.response.project_crediting_periods.find(
          (x: any) => x.crediting_period_id === Number(creditingPeriodId)
        );

        const settings = cp?.ratings?.settings || {};
        const apiUpdates = cp?.project_rating_updates || [];
        const esrExists = !!cp?.esr;
        setEsrExists(esrExists);

        form.setFieldsValue({
          ratings: {
            settings: {
              ghg_rating_review: !!settings.ghg_rating_review,
              sdg_rating_review: !!settings.sdg_rating_review,
              esr_rating_review: !!settings.esr_rating_review,
              ghg_email_notification: !!settings.ghg_email_notification,
              sdg_email_notification: !!settings.sdg_email_notification,
              esr_email_notification: !!settings.esr_email_notification
            },
            ratingUpdates: apiUpdates.map((u: any) => ({
              ...(u.id && { id: u.id }),
              type: u.type,
              title: u.name || '',
              name: u.name || '',
              update_date: u.update_date, // "YYYY-MM-01"
              description: u.description,
              email_notification_toggle: !!u.email_notification_toggle
            }))
          }
        });
      } catch {
        message.error('Failed to load status alerts');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, creditingPeriodId, form, refreshKey]);

  const settingsValues = Form.useWatch(['ratings', 'settings'], form) || {};

  const setSetting = (key: string, val: boolean) => {
    const current = form.getFieldValue(['ratings', 'settings']) || {};
    form.setFieldsValue({ ratings: { settings: { ...current, [key]: val } } });
  };

  // Rating review switch → open controlled confirm modal
  const handleReviewSwitchAttempt = (code: RatingKey, currentVal: boolean) => {
    setConfirmCode(code);
    setConfirmMode(currentVal ? 'off' : 'on');
    setConfirmOpen(true);
  };

  // Rating review confirm handlers
  const handleConfirmCancel = () => {
    setConfirmOpen(false);
    setConfirmCode(null);
    setConfirmMode(null);
  };
  const handleOnYesNotify = () => {
    if (!confirmCode) return;
    setSetting(`${confirmCode}_rating_review`, true);
    setSetting(`${confirmCode}_email_notification`, true);
    handleConfirmCancel();
  };
  const handleOnNoNotify = () => {
    if (!confirmCode) return;
    setSetting(`${confirmCode}_rating_review`, true);
    setSetting(`${confirmCode}_email_notification`, false);
    handleConfirmCancel();
  };
  const handleOffYes = () => {
    if (!confirmCode) return;
    setSetting(`${confirmCode}_rating_review`, false);
    // notification untouched
    handleConfirmCancel();
  };
  const handleOffNo = () => handleConfirmCancel();

  const SwitchRow: React.FC<{
    code: RatingKey;
    checked: boolean;
    notify: boolean;
  }> = ({ code, checked, notify }) => {
    return (
      <Space align="center" style={{ justifyContent: 'space-between', width: '100%' }}>
        <Space>
          <span>Under review</span>
          <Switch checked={checked} onChange={() => handleReviewSwitchAttempt(code, checked)} />
        </Space>
        <Space>
          <BellOutlined />
          {/* Read-only mirror; only changed via "turn ON" confirm choice */}
          <Switch
            size="small"
            checked={notify}
            disabled
            onClick={(e: any) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          />
        </Space>
      </Space>
    );
  };

  // ----- Project Updates: Add/Edit modal handlers -----
  const openAdd = () => {
    setEditingIndex(null);
    formUpdate.resetFields();
    const addOptions = esrExists ? FILTER_OUT_ESR(TYPE_OPTIONS) : TYPE_OPTIONS;
    formUpdate.setFieldsValue({
      type: addOptions[0]?.value ?? TYPE_VALUE_STATUS,
      title: '',
      description: '',
      update_date: dayjs()
    });
    setModalOpen(true);
  };

  const openEdit = (item: any, index: number) => {
    setEditingIndex(index);
    formUpdate.resetFields();
    formUpdate.setFieldsValue({
      type: item.type,
      title: item.title,
      description: item.description,
      update_date: item.update_date ? dayjs(item.update_date) : dayjs()
    });
    setModalOpen(true);
  };

  // Pre-save (validate), then ask user if they want to notify; commit after choice
  const handlePreSaveProjectUpdate = async () => {
    try {
      const v = await formUpdate.validateFields();
      if (esrExists && v.type === TYPE_VALUE_ESR && editingIndex === null) {
        message.warning('Cannot add “Potential ES Risk” after an ESR screening exists.');
        return;
      }
      const isStatus = v.type === TYPE_VALUE_STATUS;

      const entryBase = {
        ...(editingIndex !== null &&
          form.getFieldValue(['ratings', 'ratingUpdates', editingIndex, 'id']) && {
            id: form.getFieldValue(['ratings', 'ratingUpdates', editingIndex, 'id'])
          }),
        type: v.type,
        ...(isStatus && v.title && { title: v.title.trim() }),
        ...(isStatus && v.title && { name: v.title.trim() }), // API mirror
        ...(!isStatus && { title: '' }),
        ...(!isStatus && { name: '' }),
        update_date: dayjs(v.update_date).utc().format('YYYY-MM-02'),
        description: (v.description || '').trim()
        // email_notification_toggle set by confirm choice
      };

      setPendingEntry(entryBase);
      setPendingIndex(editingIndex);
      setUpdateConfirmOpen(true);
    } catch {
      // antd shows errors
    }
  };

  const commitProjectUpdate = (notify: boolean) => {
    if (!pendingEntry) return;
    const entry = { ...pendingEntry, email_notification_toggle: !!notify };

    const current = form.getFieldValue(['ratings', 'ratingUpdates']) || [];
    if (pendingIndex !== null) {
      const next = [...current];
      next[pendingIndex] = { ...next[pendingIndex], ...entry };
      form.setFieldsValue({ ratings: { ratingUpdates: next } });
    } else {
      form.setFieldsValue({ ratings: { ratingUpdates: [entry, ...current] } });
    }

    // cleanup
    setModalOpen(false);
    setEditingIndex(null);
    formUpdate.resetFields();
    setPendingEntry(null);
    setPendingIndex(null);
    setUpdateConfirmOpen(false);
  };

  const cancelProjectUpdateConfirm = () => setUpdateConfirmOpen(false);

  // ----- Delete update handlers -----
  const requestDeleteUpdate = (index: number) => {
    setDeleteIndex(index);
    setDeleteOpen(true);
  };

  const cancelDeleteUpdate = () => {
    setDeleteOpen(false);
    setDeleteIndex(null);
  };

  const confirmDeleteUpdate = () => {
    if (deleteIndex === null) return;
    const current = (form.getFieldValue(['ratings', 'ratingUpdates']) || []) as any[];
    const next = [...current];
    next[deleteIndex] = { ...next[deleteIndex], delete: true, _deleted: true };
    form.setFieldsValue({ ratings: { ratingUpdates: next } });
    setDeleteOpen(false);
    setDeleteIndex(null);
  };

  // ---------- Render project update row ----------
  const renderUpdateItem = (item: any, index: number) => {
    const displayType = TYPE_DISPLAY[item.type] || item.type;
    const isStatus = item.type === TYPE_VALUE_STATUS;
    const titleText = isStatus && item.title ? item.title : displayType;
    const dateText = item.update_date ? dayjs(item.update_date).utc().format(monthFormat) : '—';

    return (
      <List.Item key={index} style={{ padding: 0 }}>
        <div
          style={{
            width: '100%',
            border: '1px solid #eee',
            borderRadius: 12,
            padding: 16,
            background: '#fff'
          }}>
          {/* Header row */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              justifyContent: 'space-between',
              flexWrap: 'wrap'
            }}>
            <Space size={10} wrap>
              <Tag>{displayType}</Tag>
              <Typography.Text strong style={{ fontSize: 16 }}>
                {titleText}
              </Typography.Text>
            </Space>

            <Space>
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => openEdit(item, index)}>
                Edit
              </Button>
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => requestDeleteUpdate(index)}>
                Delete
              </Button>
            </Space>
          </div>

          <Divider style={{ margin: '12px 0' }} />

          {/* Body */}
          <div style={{ display: 'grid', gap: 12 }}>
            <div>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>Description</div>
              <Typography.Paragraph style={{ margin: 0 }}>
                {item.description ? (
                  <div dangerouslySetInnerHTML={{ __html: item.description }} />
                ) : (
                  <i>No description</i>
                )}
              </Typography.Paragraph>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: 16
              }}>
              <div>
                <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 2 }}>Date</div>
                <Typography.Text>{dateText}</Typography.Text>
              </div>

              <div>
                <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 2 }}>
                  Email notification
                </div>
                <Space size={6}>
                  <BellOutlined style={{ opacity: item.email_notification_toggle ? 1 : 0.35 }} />
                  <Typography.Text>
                    {item.email_notification_toggle ? 'Enabled' : 'Disabled'}
                  </Typography.Text>
                </Space>
              </div>
            </div>
          </div>

          {/* Hidden registered fields for parent form submission */}
          <div style={{ display: 'none' }}>
            <Form.Item name={[index, 'id']}>
              <Input />
            </Form.Item>
            <Form.Item name={[index, 'type']}>
              <Input />
            </Form.Item>
            <Form.Item name={[index, 'title']}>
              <Input />
            </Form.Item>
            <Form.Item name={[index, 'name']}>
              <Input />
            </Form.Item>
            <Form.Item name={[index, 'update_date']}>
              <Input />
            </Form.Item>
            <Form.Item name={[index, 'description']}>
              <Input />
            </Form.Item>
            <Form.Item name={[index, 'email_notification_toggle']} valuePropName="checked">
              <Switch />
            </Form.Item>
          </div>
        </div>
      </List.Item>
    );
  };

  // Helpers to read current values
  const ghgReview = !!settingsValues.ghg_rating_review;
  const sdgReview = !!settingsValues.sdg_rating_review;
  const esrReview = !!settingsValues.esr_rating_review;

  const ghgNotify = !!settingsValues.ghg_email_notification;
  const sdgNotify = !!settingsValues.sdg_email_notification;
  const esrNotify = !!settingsValues.esr_email_notification;

  return (
    <>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        {/* Rating Review */}
        <Card
          size="small"
          loading={loading}
          title={
            <Space>
              <span>Rating review</span>
              <Tooltip
                placement="right"
                title="Toggle review for each area. When you turn a review on, you'll choose whether to notify followers. Notifications cannot be controlled separately and remain read-only here.">
                <InfoCircleOutlined />
              </Tooltip>
            </Space>
          }>
          <Row gutter={[12, 12]}>
            <Col xs={24} md={8}>
              <Tile title="GHG">
                <SwitchRow code="ghg" checked={ghgReview} notify={ghgNotify} />
                {/* hidden registered fields */}
                <Form.Item
                  name={['ratings', 'settings', 'ghg_rating_review']}
                  valuePropName="checked"
                  noStyle>
                  <Switch style={{ display: 'none' }} />
                </Form.Item>
                <Form.Item
                  name={['ratings', 'settings', 'ghg_email_notification']}
                  valuePropName="checked"
                  noStyle>
                  <Switch style={{ display: 'none' }} />
                </Form.Item>
              </Tile>
            </Col>
            <Col xs={24} md={8}>
              <Tile title="SDG">
                <SwitchRow code="sdg" checked={sdgReview} notify={sdgNotify} />
                <Form.Item
                  name={['ratings', 'settings', 'sdg_rating_review']}
                  valuePropName="checked"
                  noStyle>
                  <Switch style={{ display: 'none' }} />
                </Form.Item>
                <Form.Item
                  name={['ratings', 'settings', 'sdg_email_notification']}
                  valuePropName="checked"
                  noStyle>
                  <Switch style={{ display: 'none' }} />
                </Form.Item>
              </Tile>
            </Col>
            <Col xs={24} md={8}>
              <Tile title="ESR">
                <SwitchRow code="esr" checked={esrReview} notify={esrNotify} />
                <Form.Item
                  name={['ratings', 'settings', 'esr_rating_review']}
                  valuePropName="checked"
                  noStyle>
                  <Switch style={{ display: 'none' }} />
                </Form.Item>
                <Form.Item
                  name={['ratings', 'settings', 'esr_email_notification']}
                  valuePropName="checked"
                  noStyle>
                  <Switch style={{ display: 'none' }} />
                </Form.Item>
              </Tile>
            </Col>
          </Row>
        </Card>

        {/* Project Updates */}
        <Card
          size="small"
          loading={loading}
          title={
            <Space>
              <span>Project updates</span>
              <Tooltip
                placement="right"
                title={
                  <>
                    Add "Status Update" or "Potential ES Risk" entries for this project. <br />
                    "Potential ES Risk" doesn't require a title; "Status Update" does.
                  </>
                }>
                <InfoCircleOutlined />
              </Tooltip>
            </Space>
          }
          extra={
            <Tooltip
              title={
                esrExists
                  ? '"Potential ES Risk" cannot be added because an ESR screening already exists.'
                  : ''
              }>
              <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
                Add Project Update
              </Button>
            </Tooltip>
          }>
          <Form.List name={['ratings', 'ratingUpdates']}>
            {(fields) => {
              const all = (form.getFieldValue(['ratings', 'ratingUpdates']) || []) as any[];
              const listData = all.filter((x: any) => !x?._deleted && !x?.delete); // HIDE deleted

              if (!listData.length) {
                return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No updates yet" />;
              }
              return (
                <List
                  dataSource={listData}
                  renderItem={(item, index) => renderUpdateItem(item, index)}
                />
              );
            }}
          </Form.List>
        </Card>

        {/* Add/Edit Update Modal */}
        <Modal
          title={editingIndex !== null ? 'Edit project update' : 'Add project update'}
          open={modalOpen}
          width={1000}
          maskClosable={false}
          centered
          onCancel={() => {
            setModalOpen(false);
            setEditingIndex(null);
            formUpdate.resetFields();
          }}
          onOk={handlePreSaveProjectUpdate}
          okText={editingIndex !== null ? 'Save' : 'Add'}
          destroyOnClose>
          <Form layout="vertical" form={formUpdate}>
            <Form.Item
              name="type"
              label="Type"
              rules={[{ required: true, message: 'Type is required' }]}>
              <Select options={effectiveTypeOptions} />
            </Form.Item>

            {/* Only show Title when type = Status Update */}
            <Form.Item noStyle shouldUpdate={(p, c) => p.type !== c.type}>
              {({ getFieldValue }) =>
                getFieldValue('type') === TYPE_VALUE_STATUS ? (
                  <Form.Item
                    name="title"
                    label="Title"
                    rules={[
                      { required: true, message: 'Title is required for Status Update' },
                      { max: 50, message: 'Max 50 characters' },
                      { whitespace: true },
                      {
                        validator: (_, value) => {
                          if (!value) return Promise.resolve();
                          const all = form.getFieldValue(['ratings', 'ratingUpdates']) || [];
                          const currentType = formUpdate.getFieldValue('type');
                          const trimmed = value.trim().toLowerCase();

                          const duplicate = all.some((u: any, i: number) => {
                            if (editingIndex !== null && i === editingIndex) return false; // allow same item during edit
                            return (
                              u.type === currentType &&
                              u.title?.trim().toLowerCase() === trimmed &&
                              !u.delete &&
                              !u._deleted // ignore deleted
                            );
                          });

                          return duplicate
                            ? Promise.reject(
                                new Error('A status update with the same title already exists')
                              )
                            : Promise.resolve();
                        }
                      }
                    ]}
                    extra="Copy title from R&A status update doc, e.g., 'Withdrawn from registry'">
                    <Input showCount maxLength={50} />
                  </Form.Item>
                ) : null
              }
            </Form.Item>

            <Form.Item
              name="description"
              label="Description"
              validateTrigger={[]}
              rules={[
                { required: true, message: 'Description is required' },
                {
                  validator: (_, v) => {
                    const text = (v || '').replace(/<[^>]*>/g, '').trim();
                    return text
                      ? Promise.resolve()
                      : Promise.reject(new Error('Please enter some description'));
                  }
                }
              ]}
              extra="Copy description from R&A status update doc">
              <SimpleEditor
                initialData={formUpdate.getFieldValue('description') || ''}
                onChange={(html: string) => {
                  formUpdate.setFieldValue('description', html);
                }}
                editable
              />
            </Form.Item>

            <Form.Item
              name="update_date"
              label="Date (month + year)"
              rules={[{ required: true, message: 'Date is required' }]}>
              <DatePicker picker="month" format={monthFormat} style={{ width: '100%' }} />
            </Form.Item>

            <Divider style={{ margin: '8px 0' }} />

            <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
              <strong>{TYPE_DISPLAY[TYPE_VALUE_ESR]}</strong> does not require a title. Multiple
              updates can be active at once.
            </Typography.Paragraph>
          </Form>
        </Modal>
      </Space>

      {/* Rating Review Confirm Modal */}
      <Modal
        open={confirmOpen}
        onCancel={handleConfirmCancel}
        footer={
          confirmMode === 'on' ? (
            <Space>
              <Button onClick={handleOnNoNotify}>No - do not notify users</Button>
              <Button type="primary" onClick={handleOnYesNotify}>
                Yes - notify users
              </Button>
            </Space>
          ) : confirmMode === 'off' ? (
            <Space>
              <Button onClick={handleOffNo}>No</Button>
              <Button type="primary" onClick={handleOffYes}>
                Yes
              </Button>
            </Space>
          ) : null
        }
        title={confirmMode === 'off' ? 'Conclude review' : 'Confirm review'}
        destroyOnClose>
        {confirmMode === 'on' && confirmCode && (
          <div>
            An email will be sent to subscribers that follow this project, notifying them that the{' '}
            <strong>{reviewNoun[confirmCode]}</strong> is under review. Is this ok?
          </div>
        )}
        {confirmMode === 'off' && <div>Are you sure you want to conclude this rating review?</div>}
      </Modal>

      {/* Post-Save Confirmation for Project Updates */}
      <Modal
        open={updateConfirmOpen}
        onCancel={cancelProjectUpdateConfirm}
        title="Confirm project update"
        footer={
          <Space>
            <Button onClick={() => commitProjectUpdate(false)}>No - do not notify users</Button>
            <Button type="primary" onClick={() => commitProjectUpdate(true)}>
              Yes - notify users
            </Button>
          </Space>
        }
        destroyOnClose>
        <div>
          An email will be sent to subscribers that follow this project, notifying them about this
          project update. Is this ok?
        </div>
      </Modal>

      {/* Delete Update Confirm Modal */}
      <Modal
        open={deleteOpen}
        onCancel={cancelDeleteUpdate}
        title="Delete project update"
        footer={
          <Space>
            <Button onClick={cancelDeleteUpdate}>No</Button>
            <Button danger type="primary" onClick={confirmDeleteUpdate}>
              Yes, delete
            </Button>
          </Space>
        }
        destroyOnClose>
        <div>Are you sure you want to delete this project update?</div>
      </Modal>
    </>
  );
};

export default StatusAlerts;
